import { inject, Injectable } from "@angular/core";
import { FileInput } from "@app/shared/components/material-file-input/material-file-input.model";
import { concatMap, forkJoin, from, last, map, Observable, of, tap, throwError } from "rxjs";
import saveAs from "file-saver";
import { NotifyService } from "@app/core/services/notify.service";
import { AppInjector } from "@app/core/functions/app-injector";
import filetypeinfo from "magic-bytes.js";
import { GuessedFile, Info } from "magic-bytes.js/dist/model/tree";
import { REGEX } from "@app/shared/constants/regex.constants";
import { FileTypes } from "@app/shared/constants/file-types.constants";
import { HttpService } from "@app/core/services/http/http.service";
import { I18nService } from "@app/core/services/i18n.service";
import { AbstractControl } from "@angular/forms";

@Injectable({
  providedIn: "root"
})
export class FileService {
  public http = inject(HttpService);

  /**
   * Retrieve document name based on path.
   * documents/test_name.pdf => test_name.pdf
   * @param path
   */
  getDocumentName(path: string): string {
    if (!path) {
      return "";
    }

    return path.split("/").pop();
  }

  /**
   * Return filename without extension
   * test.pdf => test
   * @param fileName
   * @returns
   */
  getFileNameWithoutExtension(fileName: string) {
    if (!fileName) {
      return "";
    }

    return fileName.substring(0, fileName.lastIndexOf(".")) || fileName;
  }

  /**
   * Reads the extension of the document name
   * If no extension exists; return the parm
   * @param blob
   * @returns
   */
  getDocumentType = (document: string): string => {
    if (!document) {
      return "";
    }

    const ext = document.substring(document.lastIndexOf(".") + 1, document.length);

    // Handle use cases
    switch (ext) {
      case "audio": {
        return FileTypes.WAV;
      }
      case document: {
        return "";
      }
    }

    return ext;
  };

  /**
   * Retrieve file information (extension, mime type) from blob for non plain text files
   * Plain text files will return as xml
   * @param blob
   * @returns
   */
  getFileTypeFromBlob = (blob: Blob | File): Observable<GuessedFile[]> => {
    if (!(blob instanceof Blob)) {
      return throwError(() => new Error("`blob` must be an instance of Blob."));
    }

    return new Observable((obs: any) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(blob);
      reader.onerror = (err) => obs.error(err);
      reader.onabort = (err) => obs.error(err);
      reader.onloadend = (f) => {
        const bytes = new Uint8Array(f.target.result as ArrayBuffer);
        obs.next(filetypeinfo(bytes));
        obs.complete();
      };
    });
  };

  /**
   * Create a new file input object
   * @param data
   * @param fileName : include full file name including the file type.
   */
  createFileInput(data: string | FileInput, fileName: string): FileInput {
    if (!data) {
      return null;
    }

    if (data instanceof FileInput) {
      return data;
    }

    const blob: Blob = new Blob([data]);
    const file: File = new File([blob], fileName);

    return new FileInput([file]);
  }

  /**
   * Saves a file by opening file-save-as dialog in the browser
   * using file-save library.
   * @param blobContent file content as a Blob
   * @param fileName name file should be saved as
   */
  saveDownloadedFileAs(blobContent: Blob, fileName: string, getType?: boolean) {
    if (blobContent?.size === 0 || !(blobContent instanceof Blob)) {
      this.notifyService.error("", "ERR_NO_DOCS_ERROR");
      return;
    }

    if (getType) {
      this.getFileTypeFromBlob(blobContent).subscribe({
        next: (resp) => {
          // Remove extension from existing filename if any
          const fileNoExt = this.getFileNameWithoutExtension(fileName);
          const ext = this.getExtension(resp, fileName);

          // Save blob content with file and proper ext.
          saveAs(blobContent, `${fileNoExt}.${ext.extension}`);
        },
        error: () => {
          this.notifyService.error("", "ERR_NO_DOCS_ERROR");
        }
      });
    } else {
      saveAs(blobContent, fileName);
    }
  }

  /**
   * Check file type extension.
   * Plain text files are invalid and will return as an empty array, if a plain text is identified use the extension.
   * @param fileTypes
   * @param name
   * @returns
   */
  getExtension(fileTypes: GuessedFile[], name: string): GuessedFile & Info {
    if (!fileTypes?.length && !name) {
      return {
        typename: "",
        extension: "",
        mime: ""
      };
    }

    const ext = this.getDocumentType(name) || fileTypes[0]?.extension;
    const fileType = fileTypes?.find((x) => x?.extension?.toLowerCase() === ext) ?? { typename: ext, extension: ext };

    // SVGS, other text based docs return weird values.
    if (["xml", "cf"].includes(fileType.extension)) {
      fileType.extension = ext;
    }

    return fileTypes?.length
      ? fileType
      : {
          typename: this.getDocumentType(name),
          extension: this.getDocumentType(name)
        };
  }

  /**
   * Check passed in file and make sure the name matches the mime type.
   * If the extension does not match, create a new file with proper extension.
   * @param fileTypes
   * @param file
   */
  checkForValidFileExt(fileType: GuessedFile[], file: File) {
    const fileExt = fileType[0];

    // Ignore rpts
    if (this.isReport(`${FileTypes.RPLUS}, doc`, fileType)) {
      return file;
    }

    const nameExt = this.getExtension(fileType, file.name);

    if (nameExt.extension !== fileExt.extension) {
      const blob: Blob = new Blob([file], { type: fileExt.mime });
      const newFile = new File([blob], `${this.getFileNameWithoutExtension(file.name)}.${fileExt.extension}`, { type: fileExt.mime });
      return newFile;
    } else {
      return file;
    }
  }

  /**
   * Check the internal file type to the accepted list
   * @param acceptedFileTypes
   * @param fileInfo
   */
  checkForValidFile(acceptedFileTypes: string, fileInfo: GuessedFile[]) {
    // If file is a rpt we need to check for specific type returned since .rpt is not valid
    if (this.isReport(acceptedFileTypes, fileInfo)) {
      return true;
    }

    // Check for valid extension
    if (!acceptedFileTypes?.toLowerCase()?.includes(fileInfo[0]?.extension?.toLowerCase())) {
      return false;
    }

    return true;
  }

  /**
   * Check if file is in base 64 format already and is NOT an svg image.
   */
  checkForBase64(file: string) {
    try {
      const decode = window.atob(file);
      if (this.isBase64(decode)) {
        return decode;
      }
    } catch {}

    return "";
  }

  isBase64(base64: string) {
    return REGEX.IS_BASE64.test(base64);
  }

  isImage(image: string) {
    return [FileTypes.SVG, FileTypes.JPG, FileTypes.JPEG, FileTypes.PNG, FileTypes.WEBM].includes(image as FileTypes);
  }

  /**
   * Reports do not come back correctly in fetching internal mime type, add custom logic to handle the response
   * @param acceptedFileTypes
   * @param fileInfo
   * @returns
   */
  isReport(acceptedFileTypes: string, fileInfo: GuessedFile[]) {
    if (!acceptedFileTypes.includes(FileTypes.RPLUS)) {
      return false;
    }

    if ([FileTypes.RPLUS, "doc"].includes(fileInfo[0]?.extension?.toLowerCase())) {
      return true;
    }

    return false;
  }

  /**
   * Convert MB to Bytes
   */
  convertMbToBytes(mb: number) {
    return mb * 1048576;
  }

  /**
   * Filter files before they are uploaded based on accepted file types and the
   * true mime type of the file
   * @param files
   * @param acceptedFileTypes
   * @param control
   * @returns
   */
  filterFiles(files: File[], acceptedFileTypes: string, control: AbstractControl) {
    const filteredFiles: File[] = [];

    return from(files).pipe(
      concatMap((file) => {
        return forkJoin({
          file: of(file),
          fileType: this.getFileTypeFromBlob(file)
        });
      }),
      tap((resp) => {
        if (this.checkForValidFile(acceptedFileTypes, resp.fileType)) {
          filteredFiles.push(this.checkForValidFileExt(resp.fileType, resp.file));
        } else {
          this.notifyService.error(
            "APP.ERR_TYPE",
            this.i18nService.translate("APP.ERR_TYPE_MSG", {
              fileTypes: acceptedFileTypes
            })
          );
          control?.setValue(null);
        }
      }),
      last(),
      map(() => {
        return filteredFiles;
      })
    );
  }

  get notifyService(): NotifyService {
    return AppInjector.get(NotifyService);
  }

  get i18nService(): I18nService {
    return AppInjector.get(I18nService);
  }
}
